import type { CronJob, CronExecution, QueueStatus, CreateCronJobRequest, UpdateCronJobRequest, CronNotificationSetting, ChannelConfigSummary } from '../types/cron.js';

const CRON_URL = '/api/cron';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// ─── Jobs API ───────────────────────────────────────────────────────

export async function getJobs(): Promise<CronJob[]> {
  return fetchJson<CronJob[]>(`${CRON_URL}/jobs`);
}

export async function getJob(id: number): Promise<CronJob> {
  return fetchJson<CronJob>(`${CRON_URL}/jobs/${id}`);
}

export async function createJob(data: CreateCronJobRequest): Promise<CronJob> {
  return fetchJson<CronJob>(`${CRON_URL}/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateJob(id: number, data: UpdateCronJobRequest): Promise<CronJob> {
  return fetchJson<CronJob>(`${CRON_URL}/jobs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteJob(id: number): Promise<void> {
  const response = await fetch(`${CRON_URL}/jobs/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
}

// ─── Job Actions ────────────────────────────────────────────────────

export async function triggerJob(id: number): Promise<{ message: string; jobId: number; jobName: string }> {
  return fetchJson<{ message: string; jobId: number; jobName: string }>(
    `${CRON_URL}/jobs/${id}/trigger`,
    { method: 'POST' },
  );
}

export async function toggleJob(id: number): Promise<CronJob> {
  return fetchJson<CronJob>(`${CRON_URL}/jobs/${id}/toggle`, { method: 'PATCH' });
}

// ─── Executions API ─────────────────────────────────────────────────

export async function getExecutions(jobId: number, limit?: number): Promise<CronExecution[]> {
  const params = limit ? `?limit=${limit}` : '';
  return fetchJson<CronExecution[]>(`${CRON_URL}/jobs/${jobId}/executions${params}`);
}

// ─── Queue Status API ───────────────────────────────────────────────

export async function getQueueStatus(): Promise<QueueStatus> {
  return fetchJson<QueueStatus>(`${CRON_URL}/status`);
}

// ─── Notification Settings API ──────────────────────────────────────

export async function getNotificationSettings(jobId: number): Promise<CronNotificationSetting[]> {
  return fetchJson<CronNotificationSetting[]>(`${CRON_URL}/jobs/${jobId}/notifications`);
}

export async function saveNotificationSettings(
  jobId: number,
  data: { channel_config_id: number; on_success: number; on_failure: number; on_start: number }
): Promise<CronNotificationSetting> {
  return fetchJson<CronNotificationSetting>(`${CRON_URL}/jobs/${jobId}/notifications`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteNotificationSettings(jobId: number): Promise<void> {
  const response = await fetch(`${CRON_URL}/jobs/${jobId}/notifications`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
}

// ─── Channel API (for notification settings) ────────────────────────

export async function getChannels(): Promise<ChannelConfigSummary[]> {
  return fetchJson<ChannelConfigSummary[]>('/api/messaging/channels');
}
